import { z } from "zod";

export const MAX_FOTO_LAUDO_BYTES = 10 * 1024 * 1024;
export const MAX_OBSERVACAO_PARCEIRO = 2000;
export const observacaoParceiroSchema = z.string().trim().max(MAX_OBSERVACAO_PARCEIRO).nullable()
  .transform((value) => value || null);

export function podeEditarMidiaLaudo(
  profile: { empresaId?: string | null; roles?: string[] | null; ativo?: boolean },
  laudo: { status: string; parceiroId: string; vistoriadoraId: string },
): boolean {
  if (laudo.status !== "enviado_analise" || profile.ativo === false) return false;
  if (profile.roles?.includes("owner")) return true;
  if (profile.empresaId === laudo.parceiroId) return true;
  return profile.empresaId === laudo.vistoriadoraId &&
    !!profile.roles?.some((role) => role === "admin" || role === "rt");
}

export function statusAoAtribuirTecnico<T extends string>(status: T): T | "em_vistoria" {
  return status === "rascunho" || status === "enviado_analise" ? "em_vistoria" : status;
}

/** Accept only raster images whose bytes match the declared MIME type. */
export function validarFotoLaudo(bytes: Uint8Array, mimeType: string): "jpg" | "png" | "webp" {
  if (bytes.length === 0 || bytes.length > MAX_FOTO_LAUDO_BYTES) {
    throw new Error("A foto deve ter até 10 MB e não pode estar vazia.");
  }
  const startsWith = (signature: number[], offset = 0) =>
    signature.every((byte, index) => bytes[offset + index] === byte);
  if (mimeType === "image/jpeg" && startsWith([0xff, 0xd8, 0xff])) return "jpg";
  if (mimeType === "image/png" && startsWith([137, 80, 78, 71, 13, 10, 26, 10])) return "png";
  if (mimeType === "image/webp" && startsWith([82, 73, 70, 70]) && startsWith([87, 69, 66, 80], 8)) return "webp";
  throw new Error("Selecione uma foto válida em JPG, PNG ou WebP.");
}

export type BloqueioRespostas = "sem_permissao" | "assinado" | "em_vistoria" | null;

/** Who may answer a part's checklist now. Partner hands over at inspection; signed answers must match the PDF. */
export function bloqueioRespostasLaudo(
  profile: { empresaId?: string | null; roles?: string[] | null },
  laudo: { status: string; parceiroId: string; vistoriadoraId: string },
): BloqueioRespostas {
  const owner = !!profile.roles?.includes("owner");
  const vistoriadora = !!profile.empresaId && profile.empresaId === laudo.vistoriadoraId;
  const parceiro = !!profile.empresaId && profile.empresaId === laudo.parceiroId;
  if (!owner && !vistoriadora && !parceiro) return "sem_permissao";
  if (laudo.status === "assinado" || laudo.status === "finalizado") return "assinado";
  if (!owner && !vistoriadora && laudo.status !== "rascunho" && laudo.status !== "enviado_analise") return "em_vistoria";
  return null;
}
